import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BN, DriftClient, PerpMarketConfig, User, ZERO } from '@drift-labs/sdk';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import { MarginInfo } from 'src/stores/driftStoreTypes';
import { accountTradingDataQueryKeys } from 'src/hooks/query/accountTradingData/accountTradingDataQueryKeys';
import { computeMarginInfo } from 'src/hooks/query/accountTradingData/utils/computeMarginInfo';
import { computePositions } from 'src/hooks/query/accountTradingData/utils/computePositions';
import { computeOrders } from 'src/hooks/query/accountTradingData/utils/computeOrders';
import { findStrandedIsolatedMargin } from 'src/hooks/query/accountTradingData/utils/findStrandedIsolatedMargin';
import {
	PositionsResult,
	MarginInfoByUserKey,
	PositionsByUserKey,
	OrdersByUserKey,
	LeftoverIsolatedByUserKey,
} from 'src/hooks/query/accountTradingData/utils/types';
import {
	hasMarginInfoChanged,
	hasPositionsChanged,
	hasOrdersChanged,
	hasLeftoverIsolatedChanged,
} from 'src/hooks/query/accountTradingData/utils/hasDataChanged';

const THROTTLE_MS = 50;
const POLL_INTERVAL_MS = 1000;

/**
 * Per-account hook that pushes trading data into the React Query cache.
 *
 * Follows the orderbook pattern: data is pushed via queryClient.setQueryData()
 * on SDK events, and consumers read from cache with enabled: false.
 *
 * Cache structure: single Record<string, T> per data type, keyed by userKey.
 * Each account merges its data into the shared Record via updater functions.
 *
 * Update strategy:
 * - Event-driven (userAccountUpdate, 50ms throttle): all 4 data types
 * - Polling fallback (1s): positions + margin only (depend on mark prices)
 * - Orders + leftover isolated: event-only
 *
 * Change detection: each setQueryData call is guarded by a BN-aware comparison
 * (see hasDataChanged.ts) against the current cache value, so consumers only
 * re-render when data actually changes.
 */
export function useAccountTradingDataSync(
	userKey: string,
	user: User,
	driftClient: DriftClient,
	perpMarketsLookup: PerpMarketConfig[],
	markPriceCallback: (marketIndex: number) => BN
): void {
	const queryClient = useQueryClient();

	// Use refs for frequently-changing deps so the effect doesn't re-register
	const driftClientRef = useSyncedRef(driftClient);
	const perpMarketsLookupRef = useSyncedRef(perpMarketsLookup);
	const markPriceCallbackRef = useSyncedRef(markPriceCallback);

	useEffect(() => {
		if (!user.isSubscribed) return;

		const marginKey = accountTradingDataQueryKeys.marginInfo();
		const positionsKey = accountTradingDataQueryKeys.positions();
		const ordersKey = accountTradingDataQueryKeys.orders();
		const leftoverKey = accountTradingDataQueryKeys.leftoverIsolatedPositions();

		function enrichMarginInfo(
			baseMarginInfo: ReturnType<typeof computeMarginInfo>,
			positions: PositionsResult,
			quoteInOpenOrders: BN
		): MarginInfo {
			return {
				...baseMarginInfo,
				totalUnsettledPnl: positions.totalUnsettledPnl,
				quoteInOpenOrders,
				marketsNeedingSettlement: positions.marketsNeedingSettlement,
			};
		}

		// Compute all 4 data types and push to cache (only if changed)
		function computeAndPushAll() {
			if (!user.isSubscribed) return;

			try {
				const dc = driftClientRef.current;
				const markets = perpMarketsLookupRef.current;
				const markPriceCb = markPriceCallbackRef.current;

				const positions = computePositions(user, dc, markets, markPriceCb);
				const orders = computeOrders(user, dc);
				const leftover = findStrandedIsolatedMargin(user, orders.openOrders);
				const baseMarginInfo = computeMarginInfo(user);
				const enrichedMarginInfo = enrichMarginInfo(
					baseMarginInfo,
					positions,
					orders.quoteInOpenOrders
				);

				const prevMargin =
					queryClient.getQueryData<MarginInfoByUserKey>(marginKey)?.[userKey];
				if (hasMarginInfoChanged(prevMargin, enrichedMarginInfo)) {
					queryClient.setQueryData<MarginInfoByUserKey>(marginKey, (prev) => ({
						...prev,
						[userKey]: enrichedMarginInfo,
					}));
				}

				const prevPositions =
					queryClient.getQueryData<PositionsByUserKey>(positionsKey)?.[userKey];
				if (hasPositionsChanged(prevPositions, positions)) {
					queryClient.setQueryData<PositionsByUserKey>(
						positionsKey,
						(prev) => ({
							...prev,
							[userKey]: positions,
						})
					);
				}

				const prevOrders =
					queryClient.getQueryData<OrdersByUserKey>(ordersKey)?.[userKey];
				if (hasOrdersChanged(prevOrders, orders)) {
					queryClient.setQueryData<OrdersByUserKey>(ordersKey, (prev) => ({
						...prev,
						[userKey]: orders,
					}));
				}

				const prevLeftover =
					queryClient.getQueryData<LeftoverIsolatedByUserKey>(leftoverKey)?.[
						userKey
					];
				if (hasLeftoverIsolatedChanged(prevLeftover, leftover)) {
					queryClient.setQueryData<LeftoverIsolatedByUserKey>(
						leftoverKey,
						(prev) => ({
							...prev,
							[userKey]: leftover,
						})
					);
				}
			} catch (e) {
				console.error(
					`[accountTradingData] computeAndPushAll failed for ${userKey}`,
					e
				);
			}
		}

		// Recompute only price-sensitive data (positions + margin)
		function computeAndPushPriceSensitive() {
			if (!user.isSubscribed) return;

			try {
				const dc = driftClientRef.current;
				const markets = perpMarketsLookupRef.current;
				const markPriceCb = markPriceCallbackRef.current;

				const positions = computePositions(user, dc, markets, markPriceCb);
				const baseMarginInfo = computeMarginInfo(user);

				// Read current orders from cache for margin enrichment
				const currentOrders =
					queryClient.getQueryData<OrdersByUserKey>(ordersKey)?.[userKey];

				const enrichedMarginInfo = enrichMarginInfo(
					baseMarginInfo,
					positions,
					currentOrders?.quoteInOpenOrders ?? ZERO
				);

				const prevMargin =
					queryClient.getQueryData<MarginInfoByUserKey>(marginKey)?.[userKey];
				if (hasMarginInfoChanged(prevMargin, enrichedMarginInfo)) {
					queryClient.setQueryData<MarginInfoByUserKey>(marginKey, (prev) => ({
						...prev,
						[userKey]: enrichedMarginInfo,
					}));
				}

				const prevPositions =
					queryClient.getQueryData<PositionsByUserKey>(positionsKey)?.[userKey];
				if (hasPositionsChanged(prevPositions, positions)) {
					queryClient.setQueryData<PositionsByUserKey>(
						positionsKey,
						(prev) => ({
							...prev,
							[userKey]: positions,
						})
					);
				}
			} catch (e) {
				console.error(
					`[accountTradingData] computeAndPushPriceSensitive failed for ${userKey}`,
					e
				);
			}
		}

		// Initial push
		computeAndPushAll();

		// Event-driven: userAccountUpdate with 50ms throttle
		let lastRunMs = Date.now();
		let pendingTimer: ReturnType<typeof setTimeout> | null = null;

		const eventHandler = () => {
			const now = Date.now();
			const elapsed = now - lastRunMs;
			if (elapsed >= THROTTLE_MS) {
				lastRunMs = now;
				computeAndPushAll();
			} else if (pendingTimer === null) {
				const delay = THROTTLE_MS - elapsed;
				pendingTimer = setTimeout(() => {
					pendingTimer = null;
					lastRunMs = Date.now();
					computeAndPushAll();
				}, delay);
			}
		};

		user.eventEmitter.on('userAccountUpdate', eventHandler);

		// Polling fallback: positions + margin only (mark price changes)
		const pollInterval = setInterval(
			computeAndPushPriceSensitive,
			POLL_INTERVAL_MS
		);

		return () => {
			user.eventEmitter.removeListener('userAccountUpdate', eventHandler);
			if (pendingTimer !== null) {
				clearTimeout(pendingTimer);
			}
			clearInterval(pollInterval);
		};
	}, [userKey, user, queryClient]);
}
